// Layer descriptor – runtime reprezentace jedné vrstvy v navigačním stacku

import type { LayerPresentation, LayerStatus, LayerZone } from './navigationTypes';

/** Chyba obnovy vrstvy z URL */
export interface NavigationRestoreError {
  readonly code: 'UNKNOWN_AGENDA' | 'INVALID_PARAMS' | 'DECODE_ERROR';
  readonly message: string;
  readonly agendaId?: string;
}

/** Runtime popis jedné vrstvy ve stacku */
export interface LayerDescriptor {
  /** Unikátní identita instance vrstvy */
  readonly layerId: string;
  /** Reference na registrovanou agendu */
  readonly agendaId: string;
  /** Prezentace – root nebo modal */
  readonly presentation: LayerPresentation;
  /**
   * Identita floating stacku (skupiny osamostatněných oken).
   * `undefined` = vrstva patří do výchozí plochy (docked kaskáda).
   * Osamostatnění (detach) přiřadí oknu nový unikátní stackId; navigace z floating
   * okna dědí stackId zdroje; vrácení do plochy (dock) stackId zruší.
   */
  readonly stackId?: string;
  /**
   * Zóna vrstvy v layoutu root agendy. `'detail'` nastavuje jen `openDetail` (navigace z listu /
   * treeview) — v list-detail rootu se taková vrstva vykreslí v pravém detail panelu.
   * `undefined` = běžná vrstva nad celou agendou (docked kaskáda). Zóna se NEDĚDÍ: proklik uvnitř
   * detailu (`openModal`) otevírá vrstvu nad layoutem, ne v panelu.
   */
  readonly zone?: LayerZone;
  /** Dekódované strukturální parametry */
  readonly openParams: Record<string, unknown>;
  /** Dekódovaný lokální URL stav */
  readonly stateParams: Record<string, unknown>;
  /** Runtime stav vrstvy */
  readonly status: LayerStatus;
  /** Důvod error stavu pokud status = 'error' */
  readonly errorReason?: NavigationRestoreError;
  /** Komponenta agendy pro renderování */
  readonly component?: React.ComponentType<{ layerId: string }>;
}

let layerCounter = 0;

/** Generuje unikátní layerId */
export function generateLayerId(): string {
  return `layer_${++layerCounter}_${Date.now().toString(36)}`;
}

/** Vytvoří nový layer descriptor pro programatické otevření */
export function createLayerDescriptor(params: {
  agendaId: string;
  presentation: LayerPresentation;
  openParams: Record<string, unknown>;
  stateParams?: Record<string, unknown>;
  stackId?: string;
  zone?: LayerZone;
  component?: React.ComponentType<{ layerId: string }>;
}): LayerDescriptor {
  return {
    layerId: generateLayerId(),
    agendaId: params.agendaId,
    presentation: params.presentation,
    ...(params.stackId !== undefined ? { stackId: params.stackId } : {}),
    ...(params.zone !== undefined ? { zone: params.zone } : {}),
    openParams: params.openParams,
    stateParams: params.stateParams ?? {},
    status: 'ready',
    component: params.component,
  };
}

/** Vytvoří error layer descriptor pro nevalidní vrstvu z URL */
export function createErrorLayerDescriptor(params: {
  agendaId: string;
  presentation: LayerPresentation;
  errorReason: NavigationRestoreError;
}): LayerDescriptor {
  return {
    layerId: generateLayerId(),
    agendaId: params.agendaId,
    presentation: params.presentation,
    openParams: {},
    stateParams: {},
    status: 'error',
    errorReason: params.errorReason,
  };
}
